// CoordLeaves.jsx — identical to TeacherLeaves.jsx
// Only change: API paths /teacher/leaves* → /coordinator/leaves*

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { PlusIcon, XMarkIcon, ClockIcon, CheckCircleIcon, XCircleIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .tl * { box-sizing:border-box; }
  .tl { font-family:'Sora',sans-serif; color:#1a1d2e; animation:tlFade .4s ease both; }
  @keyframes tlFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .tl-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .tl-title { font-size:1.35rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .tl-sub   { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-blue { background:#3b6ef8; color:white; } .btn-blue:hover { background:#2d5ef0; box-shadow:0 4px 14px rgba(59,110,248,.35); transform:translateY(-1px); }
  .btn-red  { background:#fef2f2; color:#ef4444; border:1px solid #fecaca; } .btn-red:hover { background:#ef4444; color:white; }
  .btn-ghost{ background:#f0f2f7; color:#6b7194; } .btn-sm { padding:6px 12px; font-size:.74rem; }
  .btn:disabled { opacity:.5; cursor:not-allowed; transform:none !important; }
  .stat-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:.9rem; margin-bottom:1.4rem; }
  .stat-card { background:white; border-radius:14px; padding:1rem 1.1rem; border:1px solid #e8ebf4; }
  .s-icon { width:32px; height:32px; border-radius:9px; display:flex; align-items:center; justify-content:center; margin-bottom:.6rem; }
  .s-icon svg { width:15px; height:15px; stroke-width:1.8; }
  .s-icon.amber{background:#fffbeb;color:#f59e0b} .s-icon.green{background:#ecfdf5;color:#10c98e}
  .s-icon.blue{background:#eef2ff;color:#3b6ef8} .s-icon.red{background:#fef2f2;color:#ef4444}
  .s-label { font-size:.67rem; font-weight:600; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .s-value { font-size:1.4rem; font-weight:700; color:#0f1123; font-family:'JetBrains Mono',monospace; }
  .table-panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; }
  .leave-table { width:100%; border-collapse:collapse; }
  .leave-table thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .leave-table th { padding:10px 16px; text-align:center !important; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .leave-table tbody tr { border-bottom:1px solid #f3f5fb; transition:background .12s; }
  .leave-table tbody tr:last-child { border-bottom:none; }
  .leave-table tbody tr:hover { background:#fafbff; }
  .leave-table td { padding:11px 16px; font-size:.8rem; vertical-align:middle; text-align:center !important; }
  .leave-cards { display:none; padding:10px; }
  .leave-card { background:#fafbff; border-radius:12px; border:1px solid #e8ebf4; padding:13px; margin-bottom:8px; }
  .lc-top { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:8px; gap:8px; }
  .lc-row { display:flex; justify-content:space-between; align-items:flex-start; font-size:.76rem; padding:4px 0; border-bottom:1px solid #f0f2f8; gap:8px; }
  .lc-row:last-of-type { border-bottom:none; }
  .lc-label { color:#9094a8; font-weight:600; white-space:nowrap; flex-shrink:0; }
  .badge { display:inline-flex; font-size:.67rem; font-weight:600; padding:3px 9px; border-radius:99px; white-space:nowrap; }
  .badge.pending{background:#fffbeb;color:#f59e0b} .badge.approved-paid{background:#ecfdf5;color:#10c98e}
  .badge.approved-unpaid{background:#eef2ff;color:#3b6ef8} .badge.rejected{background:#fef2f2;color:#ef4444}
  .type-badge { display:inline-flex; font-size:.67rem; font-weight:600; padding:3px 9px; border-radius:99px; white-space:nowrap; }
  .type-badge.sick{background:#fdf4ff;color:#a855f7} .type-badge.casual{background:#eff6ff;color:#3b82f6}
  .type-badge.emergency{background:#fff7ed;color:#f97316} .type-badge.maternity{background:#fdf2f8;color:#ec4899}
  .type-badge.annual{background:#ecfdf5;color:#10c98e} .type-badge.unpaid{background:#f9fafb;color:#6b7280}
  .type-badge.other{background:#f0f2f7;color:#6b7194}
  .date-range { font-family:'JetBrains Mono',monospace; font-size:.74rem; color:#1a1d2e; font-weight:500; }
  .date-days  { font-size:.67rem; color:#9094a8; margin-top:2px; }
  .admin-note { font-size:.72rem; color:#6b7194; font-style:italic; }
  .tl-empty { text-align:center; padding:3.5rem 2rem; }
  .tl-empty-icon { width:48px; height:48px; border-radius:13px; background:#fffbeb; color:#f59e0b; display:flex; align-items:center; justify-content:center; margin:0 auto 12px; }
  .tl-empty-icon svg { width:22px; height:22px; stroke-width:1.6; }
  .tl-empty-title { font-size:.9rem; font-weight:600; color:#1a1d2e; margin-bottom:4px; }
  .tl-empty-sub   { font-size:.76rem; color:#9094a8; }
  .tl-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#10c98e; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }
  .modal-backdrop { position:fixed; inset:0; background:rgba(15,17,35,.55); backdrop-filter:blur(4px); display:flex; align-items:center; justify-content:center; z-index:1000; padding:20px; }
  .modal { background:white; border-radius:20px; padding:26px; width:100%; max-width:480px; max-height:90vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.2); animation:modalIn .25s ease; }
  @keyframes modalIn { from{opacity:0;transform:scale(.94)} to{opacity:1;transform:scale(1)} }
  .modal-head  { display:flex; align-items:center; justify-content:space-between; margin-bottom:18px; }
  .modal-title { font-size:1rem; font-weight:700; color:#0f1123; }
  .modal-close { width:30px; height:30px; border-radius:8px; border:none; background:#f0f2f7; color:#6b7194; cursor:pointer; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .modal-close svg { width:15px; height:15px; stroke-width:2; }
  .modal-btns { display:flex; gap:8px; justify-content:flex-end; margin-top:20px; flex-wrap:wrap; }
  .form-grid  { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
  .form-grid > *, .fg > * { min-width:0; }
  .form-field { display:flex; flex-direction:column; gap:5px; }
  .form-field.span2 { grid-column:1/-1; }
  .form-label { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .form-input,.form-select,.form-textarea { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
  .form-input:focus,.form-select:focus,.form-textarea:focus { border-color:#3b6ef8; background:white; }
  .form-textarea { resize:vertical; min-height:70px; }
  @media (max-width:800px) { .stat-grid { grid-template-columns:repeat(2,1fr); } }
  @media (max-width:768px) {
    .tl-title { font-size:1.1rem; }
    .leave-table-wrap { display:none !important; }
    .leave-cards { display:block; }
    .modal { padding:18px; }
    .form-grid { grid-template-columns:1fr; }
    .form-field.span2 { grid-column:1; }
    .modal-btns { justify-content:stretch; }
    .modal-btns .btn { flex:1; justify-content:center; }
  }
`;

const fmtDate   = (d) => new Date(d).toLocaleDateString('en-US', { day:'numeric', month:'short', year:'numeric' });
const dayCount  = (s, e) => Math.round((new Date(e)-new Date(s))/86400000)+1;
const typeLabel   = { sick:'Sick', casual:'Casual', emergency:'Emergency', maternity:'Maternity', annual:'Annual', unpaid:'Unpaid', other:'Other' };
const statusLabel = { pending:'Pending', 'approved-paid':'Approved (Paid)', 'approved-unpaid':'Approved (Unpaid)', rejected:'Rejected' };

export default function CoordLeaves() {
  const [leaves,    setLeaves]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [applyOpen, setApplyOpen] = useState(false);
  const [submitting,setSubmitting]= useState(false);
  const [form, setForm] = useState({ startDate:'', endDate:'', type:'sick', reason:'' });
  const [quota, setQuota] = useState(null);

  useEffect(() => { fetchLeaves(); }, []);
  useEffect(() => {
    api.get('/coordinator/leaves/quota').then(r => setQuota(r.data?.stats||null)).catch(() => {});
  }, []);

  const fetchLeaves = async () => {
    try { setLoading(true); const { data } = await api.get('/coordinator/leaves'); setLeaves(data); }
    catch { toast.error('Failed to load leaves'); } finally { setLoading(false); }
  };

  const handleApply = async () => {
    if (!form.startDate||!form.endDate) { toast.error('Please select dates'); return; }
    if (!form.reason.trim()) { toast.error('Reason is required'); return; }
    try {
      setSubmitting(true);
      await api.post('/coordinator/leaves', form);
      toast.success('Leave application submitted');
      setApplyOpen(false); setForm({ startDate:'', endDate:'', type:'sick', reason:'' }); fetchLeaves();
    } catch (err) {
      const d = err.response?.data;
      if (d?.quotaExceeded) toast.error(d.message, { duration:5000 });
      else if (d?.offDayBlocked) toast.error('Cannot apply leave on off-days or holidays', { duration:4000 });
      else toast.error(d?.message||'Failed to apply');
    } finally { setSubmitting(false); }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this leave application?')) return;
    try { await api.delete(`/coordinator/leaves/${id}`); toast.success('Leave cancelled'); fetchLeaves(); }
    catch (err) { toast.error(err.response?.data?.message||'Failed'); }
  };

  const pending        = leaves.filter(l => l.status==='pending').length;
  const approvedPaid   = leaves.filter(l => l.status==='approved-paid').length;
  const approvedUnpaid = leaves.filter(l => l.status==='approved-unpaid').length;
  const rejected       = leaves.filter(l => l.status==='rejected').length;

  return (
    <div className="tl"><style>{style}</style>
      <div className="tl-header">
        <div><div className="tl-title">My Leaves</div><div className="tl-sub">Apply for leave and track application status</div></div>
        <button className="btn btn-blue" onClick={() => setApplyOpen(true)}><PlusIcon />Apply for Leave</button>
      </div>

      <div className="stat-grid">
        <div className="stat-card"><div className="s-icon amber"><ClockIcon /></div><div className="s-label">Pending</div><div className="s-value">{pending}</div></div>
        <div className="stat-card"><div className="s-icon green"><CheckCircleIcon /></div><div className="s-label">Approved Paid</div><div className="s-value">{approvedPaid}</div></div>
        <div className="stat-card"><div className="s-icon blue"><CheckCircleIcon /></div><div className="s-label">Approved Unpaid</div><div className="s-value">{approvedUnpaid}</div></div>
        <div className="stat-card"><div className="s-icon red"><XCircleIcon /></div><div className="s-label">Rejected</div><div className="s-value">{rejected}</div></div>
      </div>

      <div className="table-panel">
        {loading ? (
          <div className="tl-loading"><div className="spinner"/>Loading…</div>
        ) : leaves.length===0 ? (
          <div className="tl-empty">
            <div className="tl-empty-icon"><CalendarDaysIcon /></div>
            <div className="tl-empty-title">No leave applications</div>
            <div className="tl-empty-sub">Click "Apply for Leave" to submit your first application.</div>
          </div>
        ) : (
          <>
            <div className="leave-table-wrap" style={{overflowX:'auto'}}>
              <table className="leave-table">
                <thead><tr><th>Type</th><th>Dates</th><th>Reason</th><th>Applied</th><th>Status</th><th>Admin Note</th><th></th></tr></thead>
                <tbody>
                  {leaves.map(l => (
                    <tr key={l._id}>
                      <td><span className={`type-badge ${l.type}`}>{typeLabel[l.type]||l.type}</span></td>
                      <td><div className="date-range">{fmtDate(l.startDate)}{l.startDate!==l.endDate&&` → ${fmtDate(l.endDate)}`}</div><div className="date-days">{dayCount(l.startDate,l.endDate)} day{dayCount(l.startDate,l.endDate)>1?'s':''}</div></td>
                      <td style={{maxWidth:160,fontSize:'.76rem',color:'#6b7194'}}>{l.reason||<span style={{color:'#c0c5dc'}}>—</span>}</td>
                      <td style={{fontSize:'.74rem',color:'#6b7194',fontFamily:"'JetBrains Mono',monospace"}}>{fmtDate(l.appliedAt)}</td>
                      <td><span className={`badge ${l.status}`}>{statusLabel[l.status]}</span></td>
                      <td>{l.adminNote?<div className="admin-note">"{l.adminNote}"</div>:<span style={{color:'#c0c5dc',fontSize:'.74rem'}}>—</span>}</td>
                      <td>{l.status==='pending'&&<button className="btn btn-red btn-sm" onClick={()=>handleCancel(l._id)}>Cancel</button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="leave-cards">
              {leaves.map(l => (
                <div key={l._id} className="leave-card">
                  <div className="lc-top">
                    <div>
                      <span className={`type-badge ${l.type}`} style={{marginBottom:4,display:'inline-flex'}}>{typeLabel[l.type]||l.type}</span>
                      <div className="date-range" style={{marginTop:4}}>{fmtDate(l.startDate)}{l.startDate!==l.endDate&&` → ${fmtDate(l.endDate)}`}</div>
                      <div className="date-days">{dayCount(l.startDate,l.endDate)} day{dayCount(l.startDate,l.endDate)>1?'s':''}</div>
                    </div>
                    <span className={`badge ${l.status}`}>{statusLabel[l.status]}</span>
                  </div>
                  {l.reason&&<div className="lc-row"><span className="lc-label">Reason</span><span style={{fontSize:'.74rem',color:'#6b7194',textAlign:'right'}}>{l.reason}</span></div>}
                  <div className="lc-row"><span className="lc-label">Applied</span><span style={{fontSize:'.72rem',fontFamily:"'JetBrains Mono',monospace",color:'#6b7194'}}>{fmtDate(l.appliedAt)}</span></div>
                  {l.adminNote&&<div className="lc-row"><span className="lc-label">Admin Note</span><div className="admin-note" style={{textAlign:'right'}}>"{l.adminNote}"</div></div>}
                  {l.status==='pending'&&<div style={{marginTop:8}}><button className="btn btn-red btn-sm" style={{width:'100%',justifyContent:'center'}} onClick={()=>handleCancel(l._id)}>Cancel Application</button></div>}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {applyOpen && createPortal(
        <div className="modal-backdrop" onClick={()=>setApplyOpen(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-head"><div className="modal-title">Apply for Leave</div><button className="modal-close" onClick={()=>setApplyOpen(false)}><XMarkIcon /></button></div>
            <div className="form-grid">
              <div className="form-field span2">
                <label className="form-label">Leave Type</label>
                <select className="form-select" value={form.type} onChange={e=>setForm({...form,type:e.target.value})}>
                  <option value="sick">Sick Leave</option>
                  <option value="casual">Casual Leave</option>
                  <option value="emergency">Emergency Leave</option>
                  <option value="maternity">Maternity Leave</option>
                  <option value="annual">Annual Leave</option>
                  <option value="unpaid">Unpaid Leave</option>
                  <option value="other">Other</option>
                </select>
                {quota && (() => {
                  const q = quota.find(x => x.type===form.type);
                  if (!q) return null;
                  const isUnlimited = q.quota==='unlimited'||Number(q.quota)>=999;
                  const pct = isUnlimited?0:Math.round((q.used/q.quota)*100);
                  const color = isUnlimited?'#9094a8':pct>=100?'#ef4444':pct>=80?'#f59e0b':'#10c98e';
                  return (
                    <div style={{marginTop:6,padding:'8px 10px',borderRadius:9,background:'#f8f9fc',border:`1.5px solid ${color}30`}}>
                      <div style={{display:'flex',justifyContent:'space-between',fontSize:'.74rem',fontWeight:700,marginBottom:isUnlimited?0:5}}>
                        <span style={{color:'#6b7194'}}>Quota this year</span>
                        <span style={{color}}>{isUnlimited?'Unlimited':`${q.remaining} days remaining`}</span>
                      </div>
                      {!isUnlimited&&<div style={{height:5,background:'#e0e4f0',borderRadius:99,overflow:'hidden'}}>
                        <div style={{height:'100%',width:`${Math.min(100,pct)}%`,background:color,borderRadius:99}}/>
                      </div>}
                      <div style={{fontSize:'.68rem',color:'#9094a8',marginTop:3}}>
                        Policy: <b style={{textTransform:'capitalize'}}>{q.policy}</b>
                        {!isUnlimited&&<> · Used {q.used} of {q.quota} days</>}
                        {pct>=100&&<span style={{color:'#ef4444',fontWeight:700}}> · QUOTA FULL</span>}
                      </div>
                    </div>
                  );
                })()}
              </div>
              <div className="form-field"><label className="form-label">Start Date</label><input type="date" className="form-input" value={form.startDate} onChange={e=>setForm({...form,startDate:e.target.value})} /></div>
              <div className="form-field"><label className="form-label">End Date</label><input type="date" className="form-input" value={form.endDate} min={form.startDate} onChange={e=>setForm({...form,endDate:e.target.value})} /></div>
              <div className="form-field span2"><label className="form-label">Reason</label><textarea className="form-textarea" required value={form.reason} onChange={e=>setForm({...form,reason:e.target.value})} placeholder="Describe your reason…" /></div>
            </div>
            <div className="modal-btns">
              <button className="btn btn-ghost" onClick={()=>setApplyOpen(false)}>Cancel</button>
              <button className="btn btn-blue" onClick={handleApply} disabled={submitting}>{submitting?'Submitting…':'Submit Application'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}
    </div>
  );
}